import { RouterStore } from 'mobx-react-router';
import { CurrentUserState, FlagKeys } from '../../../../mainv2/states/CurrentUserState';
import { CreditCardState } from '../../../components/molecules/payment/states/CreditCardFormState';
import {
    ApplicationPage,
    ApplicationPageKey,
    MULTICREATOR_APPLICATION_PAGE_KEY,
} from '../../../types/applicationPages';
import { action, computed, observable } from 'mobx';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { ApplicationPageApis } from '../../../apis/applicationPages';
import { EnhancedLocationDescriptor, V3Routes } from '../../../routes';
import { UserFormState } from './UserFormState';
import { SheRavenClient } from '../../../../commonv2/modules/ravenClient';
import moment from 'moment-timezone';
import { CouponState } from './CouponState';
import { BaseListState } from '../../../../commonv3/states/LocationListState';
import { LocationId } from '../../../../commonv3/types/locations';
import { ServiceAgreementKey, ServiceAgreementState } from './../../../states/ServiceAgreementState';
import { ISODate } from '../../../../commonv3/types/dates';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { BaseApplicationPageState } from './BaseApplicationPageState';

export class CustomApplicationPageState implements BaseApplicationPageState {
    userState: CurrentUserState = null;
    routerState: RouterStore = null;
    cardState: CreditCardState = null;
    page_key: ApplicationPageKey = null;
    name = null;
    userFormState: UserFormState = new UserFormState();
    baseListState: BaseListState = new BaseListState();
    serviceAgreementState: ServiceAgreementState = new ServiceAgreementState();
    @observable applicationPageResource: RemoteResourceV3<ApplicationPage> = RemoteResourceV3.loading();
    @observable couponState = new CouponState();
    @observable submitState = new SubmitState();
    @observable initState = new SubmitState();
    @observable startDate = null;
    @observable error = null;
    @observable startedFrom: EnhancedLocationDescriptor = null;
    @observable entranceInstallments = 1;
    @observable selectedLocationId = -1;

    constructor(userState: CurrentUserState, routerState: RouterStore) {
        this.userState = userState;
        this.routerState = routerState;
        this.cardState = new CreditCardState(userState);
    }

    async init(page_key: ApplicationPageKey) {
        this.page_key = page_key;
        // MCC Ph2本リリース後は、専用の申込画面にリダイレクトする
        if (
            this.page_key === MULTICREATOR_APPLICATION_PAGE_KEY &&
            this.userState.hasEnabledFlagKey(FlagKeys.MULTICREATOR_PH2)
        ) {
            this.routerState.push(V3Routes.multicreatorApplication());
            return;
        }
        await this.initState.operate(async () => {
            return await Promise.all([
                this.reloadApplicationPage(),
                this.userState.reloadUser(),
                this.cardState.init(),
                this.baseListState.init(),
            ]);
        });

        if (this.expiredMulticreatorApplyPage()) {
            return GlobalStatesV3.routing.push(V3Routes.multicreatorFull());
        }

        if (this.applicationPageResource.isNotFound() || this.applicationPageResource.isOperationalError()) {
            this.routerState.replace('');
        } else if (this.applicationPage && !this.applicationPage.qualified) {
            this.routerState.replace('');
        } else {
            if (!this.userState.isSignedIn()) {
                this.userFormState.init();
                this.startedFrom = V3Routes.applicationPageUserInfo(this.page_key);
            } else {
                this.userFormState.init(this.userState.currentUser);
                this.startedFrom = V3Routes.applicationPageUserSummary(this.page_key);
            }
            this.serviceAgreementState.init(this.applicationPage.service_agreement_key || ServiceAgreementKey.STANDARD);
            this.routerState.replace(this.startedFrom);
        }
    }

    async reloadApplicationPage() {
        try {
            await updateRemoteResourceV3(
                this.applicationPageResource,
                (v) => (this.applicationPageResource = v),
                () => ApplicationPageApis.show(this.page_key, this.userEmail, this.couponState.appliedCouponCode)
            );
            this.name = this.applicationPage.name;
        } catch (error) {
            this.routerState.replace(V3Routes.home());
        }
    }

    @computed
    get applicationPage(): ApplicationPage {
        return this.applicationPageResource.get();
    }

    @computed
    get totalPrice() {
        const entranceFee = this.applicationPage.entrance_fee_item;
        let price = this.applicationPage.membership_item.total_price;
        if (entranceFee) {
            const option = entranceFee.options.find((o) => o.installments === this.entranceInstallments);
            price += option.monthly_payments[0];
        }
        return price;
    }

    @computed
    get canGoBack(): boolean {
        if (!this.startedFrom) {
            return false;
        }
        return this.startedFrom.pathname !== this.routerState.location.pathname;
    }

    @computed
    get userEmail() {
        if (this.userState.isSignedIn()) {
            return this.userState.currentUser.email;
        }
        return this.userFormState.fields.email.getDirtyValue();
    }

    @computed
    get membershipPlanId() {
        return this.applicationPage.membership_item.membership_plan_id;
    }

    @action.bound
    async setEntranceInstallments(installments: number) {
        this.entranceInstallments = installments;
    }

    @action.bound
    setStartDate(date) {
        this.startDate = moment(date);
    }

    @action.bound
    async validateStartDateAndNext() {
        if (!this.startDate) {
            return;
        }
        this.routerState.push(V3Routes.applicationPageHomeLocationSelect(this.page_key));
    }

    @action.bound
    async validateUserAndNext() {
        await this.submitState.operate(async () => {
            await this.userFormState.validate();
            if (this.userFormState.hasErrors()) {
                return;
            }
            if (this.hasSingleFixedEffectiveDate(this.applicationPage.fixed_effective_dates)) {
                this.setStartDate(this.applicationPage.fixed_effective_dates[0]);
                this.routerState.push(V3Routes.applicationPageHomeLocationSelect(this.page_key));
            } else {
                this.routerState.push(V3Routes.applicationPageStartDateSelect(this.page_key));
            }
        });
    }

    hasSingleFixedEffectiveDate(fixed_effective_dates: Array<ISODate>): boolean {
        if (fixed_effective_dates.length == 1) {
            return true;
        } else {
            return false;
        }
    }

    expiredMulticreatorApplyPage(): boolean {
        if (
            this.page_key == MULTICREATOR_APPLICATION_PAGE_KEY &&
            this.applicationPageResource.isError() &&
            this.applicationPageResource.error.body == '有効期限が切れています'
        ) {
            return true;
        } else {
            return false;
        }
    }

    @action.bound
    async validateCardAndNext() {
        await this.submitState.operate(async () => {
            if (this.cardState.editMode) {
                if (!(await this.cardState.validateNewCard())) {
                    return;
                }
            }
            this.error = null;
            this.routerState.push(V3Routes.applicationPageConfirmation(this.page_key));
        });
    }

    @action.bound
    async validateCouponCode() {
        await this.submitState.operate(async () => {
            await this.couponState.submit(this.userEmail, this.membershipPlanId);
            if (this.couponState.appliedCouponCode) {
                await Promise.all([this.reloadApplicationPage()]);
            }
        });
    }

    @action.bound
    selectHomeLocation(locationId: LocationId | null) {
        this.selectedLocationId = locationId;
    }

    @action.bound
    async validateHomeLocationAndNext() {
        if (this.selectedLocationId != null && this.selectedLocationId <= 0) {
            throw new Error('Home location must be selected');
        }
        this.routerState.push(V3Routes.applicationPageSummary(this.page_key));
    }

    @action.bound
    async validateFeeSummaryAndNext() {
        return GlobalStatesV3.routing.push(V3Routes.applicationPagePaymentMethod(this.page_key));
    }

    @action.bound
    async clearCoupons() {
        await this.submitState.operate(async () => {
            this.couponState.clearCoupons();
            await Promise.all([this.reloadApplicationPage()]);
        });
    }

    @computed
    get entranceFeeItemName(): string {
        return this.applicationPage.membership_item.price > 0 ? '入会金' : this.applicationPage.entrance_fee_item.name;
    }

    @computed
    get submitting(): boolean {
        return this.submitState.submitting;
    }

    @action.bound
    async submit() {
        try {
            await this.submitState.operate(async () => {
                this.error = null;
                const response = await ApplicationPageApis.apply(this.page_key, {
                    user: this.userState.isSignedIn() ? undefined : this.userFormState.getDirtyValue(),
                    card: this.cardState.newToken ? { token: this.cardState.newToken } : undefined,
                    entrance_installments: this.entranceInstallments,
                    coupon_code: this.couponState.appliedCouponCode,
                    effective_date: this.startDate.format('YYYY-MM-DD'),
                    home_location_id: this.selectedLocationId,
                });
                await this.userState.reloadUser();
                // Handle partial failures of orders
                if (response.orders.find((order) => order.payments.some((o) => o.status === 'error'))) {
                    this.error = response.orders;
                }
            });
            this.routerState.push(V3Routes.applicationPageComplete(this.page_key));
        } catch (e) {
            SheRavenClient.captureException(e, {
                user: this.userState.currentUser,
                applicationPage: this.applicationPage,
            });
            this.error = e;
        }
    }

    @action.bound
    complete() {
        // A temporary solution to force refresh the user info
        // API client doesn't track the cookie update
        location.href = V3Routes.home().stringify();
    }

    @action.bound
    completeWithError() {
        location.href = V3Routes.payments().stringify();
    }

    @action.bound
    restart() {
        location.href = V3Routes.application().stringify();
    }
}
